/**
 * Team identity panel loading branding and asset records. Admin UI controls edit workspace
 * identity, colors, and uploaded assets; server authorization remains authoritative.
 *
 * @module apps/web/src/components/teams/TeamBranding
 */

import type { TeamBrandAsset, TeamBrandingProfile } from '@fluxability/types';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { API_BASE, request } from '@/lib/api';
import {
  Field,
  inputClass,
  Panel,
  ResourceState,
  Status,
  teamPath,
  teamRequest,
  useTeamResource,
  type TeamPanelProps,
} from './shared';

/**
 * Fetch a team asset with cookie/retained bearer credentials, accept PNG/JPEG/WebP content, and
 * display a temporary object URL or name initial. Aborts the request and revokes the object URL on
 * cleanup.
 */
export function BrandAssetImage({
  teamId,
  assetId,
  name,
}: {
  /** Selected team ID used to scope API requests; the server validates membership and permission. */
  teamId: string;
  /** Team asset ID fetched through the authenticated content endpoint. */
  assetId: string;
  /** Team display name used for labels and the fallback identity initial. */
  name: string;
}) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    const controller = new AbortController();
    let objectUrl = '';
    const token = localStorage.getItem('flexiplanner-session-token');
    fetch(`${API_BASE}${teamPath(teamId, `brand-assets/${encodeURIComponent(assetId)}/content`)}`, {
      credentials: 'include',
      signal: controller.signal,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(async response => {
        if (
          !response.ok ||
          !/^image\/(png|jpeg|webp)(;|$)/i.test(response.headers.get('Content-Type') || '')
        )
          return;
        const blob = await response.blob();
        if (controller.signal.aborted) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {});
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [teamId, assetId]);
  return url ? (
    <img className="h-12 w-12 rounded-md object-contain" src={url} alt={`${name} logo`} />
  ) : (
    <span
      className="bg-muted flex h-12 w-12 items-center justify-center rounded-md font-semibold"
      aria-label={`${name} initial`}
    >
      {name.slice(0, 1)}
    </span>
  );
}
/** The three palette entries of a branding profile. */
type ColorField = 'primary_color' | 'accent_color' | 'surface_color';

/** Use a stored `#rrggbb` color, or the fallback when it is missing or malformed. */
function colorOrFallback(value: string | null | undefined, fallback: string): string {
  return value && /^#[\da-f]{6}$/i.test(value) ? value : fallback;
}

/**
 * Team identity panel loading branding and asset records. Admin UI controls edit workspace identity, colors, and uploaded assets; server authorization remains authoritative.
 */
export function TeamBranding({ teamId, isAdmin }: TeamPanelProps) {
  const resource = useTeamResource<{ /** Stored or default branding. */ branding: TeamBrandingProfile }>(
    teamPath(teamId, 'branding')
  );
  const branding = resource.data?.branding;
  return (
    <Panel title="Workspace identity" description="Set a workspace name, logo, and palette.">
      <ResourceState resource={resource} />
      {branding && (
        <BrandingEditor
          key={JSON.stringify(branding)}
          teamId={teamId}
          isAdmin={isAdmin}
          branding={branding}
          onSaved={resource.reload}
        />
      )}
    </Panel>
  );
}
/** Edit a loaded team identity and manage approved raster assets. Upload checks MIME type, size, and dimensions before the API call; busy/error state stays local and successful saves request a parent reload. */
function BrandingEditor({
  teamId,
  isAdmin,
  branding,
  onSaved,
}: {
  /** Selected team ID used to scope API requests; the server validates membership and permission. */
  teamId: string;
  /** Server-derived UI capability hint for displaying admin controls; every API operation still checks permission. */
  isAdmin: boolean;
  /** Loaded identity record used to initialize editable fields; the parent keys the editor by this record. */
  branding: TeamBrandingProfile;
  /** Reload branding after a successful identity save. */
  onSaved: () => void;
}) {
  const assets = useTeamResource<{ /** Uploaded logos, newest first. */ assets: TeamBrandAsset[] }>(
    teamPath(teamId, 'brand-assets')
  );
  const [logo, setLogo] = useState(branding.logo_asset_id || '');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  async function upload(file?: File) {
    if (!file) return;
    if (
      !['image/png', 'image/jpeg', 'image/webp'].includes(file.type) ||
      !file.size ||
      file.size > 2_000_000
    ) {
      setMessage('Choose a PNG, JPEG, or WebP image no larger than 2 MB.');
      return;
    }
    setBusy(true);
    try {
      const bitmap = await createImageBitmap(file);
      const valid = bitmap.width <= 2048 && bitmap.height <= 2048;
      bitmap.close();
      if (!valid) throw new Error('The image must be at most 2048 × 2048 pixels.');
      const result = await request<{ /** The stored logo. */ asset: TeamBrandAsset }>(teamPath(teamId, 'brand-assets'), {
        method: 'POST',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!result.success) throw new Error(result.error?.message || 'Upload failed.');
      const id = result.data?.asset?.id;
      if (!id) throw new Error('The server did not return an asset ID.');
      setLogo(id);
      assets.reload();
      setMessage('Logo uploaded. Save identity to apply it.');
    } catch (error) {
      setMessage((error as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <form
        className="grid gap-3 sm:grid-cols-2"
        onSubmit={async event => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          setBusy(true);
          try {
            await teamRequest(teamPath(teamId, 'branding'), 'PUT', {
              display_name: String(form.get('display_name')),
              logo_asset_id: logo || null,
              primary_color: form.get('primary_color'),
              accent_color: form.get('accent_color'),
              surface_color: form.get('surface_color'),
            });
            setMessage('Workspace identity saved.');
            window.dispatchEvent(
              new CustomEvent('fluxability-branding-updated', { detail: teamId })
            );
            onSaved();
          } catch (error) {
            setMessage((error as Error).message);
          } finally {
            setBusy(false);
          }
        }}
      >
        <Field label="Display name">
          <input
            className={inputClass}
            name="display_name"
            defaultValue={branding.display_name || 'Fluxability'}
            disabled={!isAdmin || busy}
            required
          />
        </Field>
        {[
          ['primary_color', 'Primary', '#0f766e'],
          ['accent_color', 'Accent', '#eab308'],
          ['surface_color', 'Surface', '#ffffff'],
        ].map(([name, label, fallback]) => (
          <Field key={name} label={label}>
            <input
              type="color"
              className="h-10 w-full rounded-md border"
              name={name}
              defaultValue={colorOrFallback(branding[name as ColorField], fallback)}
              disabled={!isAdmin || busy}
            />
          </Field>
        ))}
        <fieldset className="space-y-3 sm:col-span-2" disabled={!isAdmin || busy}>
          <legend className="mb-2 text-sm font-medium">Team logo</legend>
          {isAdmin && (
            <Field label="Upload PNG, JPEG, or WebP (2 MB maximum)">
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={event => void upload(event.target.files?.[0])}
              />
            </Field>
          )}
          <ResourceState resource={assets} />
          <div className="flex flex-wrap gap-3">
            <Button
              type="button"
              variant={logo ? 'outline' : 'secondary'}
              aria-pressed={!logo}
              onClick={() => setLogo('')}
            >
              No logo
            </Button>
            {(assets.data?.assets || []).map(asset => (
              <div className="space-y-2 rounded-md border p-2" key={asset.id}>
                <button
                  className="flex flex-col items-center gap-1 p-2 text-xs"
                  type="button"
                  aria-pressed={logo === asset.id}
                  onClick={() => setLogo(asset.id)}
                >
                  <BrandAssetImage
                    teamId={teamId}
                    assetId={asset.id}
                    name={branding.display_name || 'Team'}
                  />
                  <span>
                    {logo === asset.id ? 'Selected' : 'Use logo'} · {asset.width_px} ×{' '}
                    {asset.height_px}
                  </span>
                </button>
                {isAdmin && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      setBusy(true);
                      try {
                        await teamRequest(
                          teamPath(teamId, `brand-assets/${encodeURIComponent(asset.id)}`),
                          'DELETE'
                        );
                        if (logo === asset.id) setLogo('');
                        assets.reload();
                      } catch (error) {
                        setMessage((error as Error).message);
                      } finally {
                        setBusy(false);
                      }
                    }}
                  >
                    Delete logo
                  </Button>
                )}
              </div>
            ))}
          </div>
        </fieldset>
        {isAdmin && <Button disabled={busy}>Save identity</Button>}
      </form>
      <Status message={message} />
    </>
  );
}
